// Debug mode lets us verify events locally: trackEvent fires (and logs) outside
// production so the funnel can be walked with `NEXT_PUBLIC_ANALYTICS_DEBUG=true`.
import {
  LAST_TOUCH_ATTRIBUTION_COOKIE,
  LAST_TOUCH_ATTRIBUTION_STORAGE_KEY,
  type StoredAttribution,
} from "@/lib/attribution-cookies";

const debugEnabled = process.env.NEXT_PUBLIC_ANALYTICS_DEBUG === "true";
const shouldTrack = process.env.NODE_ENV === "production" || debugEnabled;
const umamiQueueKey = "__hyredUmamiQueue";
const analyticsDelivery =
  process.env.NEXT_PUBLIC_ANALYTICS_DELIVERY === "zaraz" ? "zaraz" : "gtm";

// `signup` and `sign_up_success` are two names for one moment, and
// AnalyticsAuthEvents fires both. While both reached the dataLayer, GTM turned
// them into two GA4 events, so the property recorded roughly 1.400 `sign_up`
// and 1.400 `sign_up_success` per 28 days for the same ~1.400 signups. GA4 now
// receives `sign_up` from the server instead, once per inserted users row, via
// recordSignupAnalytics in src/lib/auth/signup-analytics.ts. Umami and PostHog
// keep both client names so their historical reports stay continuous.
const ga4SuppressedEventNames = new Set(["signup", "sign_up_success"]);

const adsKeyEventNames = new Set([
  // Primary lead/conversion candidates for Google Ads import from GA4.
  "review_started",
  // purchase and sign_up are recorded server-side only, via GA4 Measurement
  // Protocol in src/lib/payments/payment-analytics.ts and
  // src/lib/auth/signup-analytics.ts, not through this client dataLayer path.
  // purchase is kept here so ads_key_event still marks it correctly if anything
  // ever calls trackEvent("purchase", ...) from the client again.
  "purchase",
]);

function toSnakeCase(value: string) {
  return value
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1_$2")
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[-\s]+/g, "_")
    .toLowerCase();
}

function normalizeEventData(data?: UmamiEventData) {
  if (!data) {
    return undefined;
  }

  return Object.fromEntries(
    Object.entries(data).map(([key, value]) => [toSnakeCase(key), value]),
  ) as UmamiEventData;
}

function createEventId(eventName: string) {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return `${eventName}_${crypto.randomUUID()}`;
  }

  return `${eventName}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function getRestoredAttributionEventData(): UmamiEventData | undefined {
  const attribution = readStoredAttributionForAnalytics();

  if (!attribution) {
    return undefined;
  }

  return {
    attribution_restored: true,
    utm_source: attribution.utmSource,
    utm_medium: attribution.utmMedium,
    utm_campaign: attribution.utmCampaign,
    utm_content: attribution.utmContent,
    utm_term: attribution.utmTerm,
    gclid: attribution.gclid,
    fbclid: attribution.fbclid,
    referrer: attribution.referrer,
    attribution_landing_path: attribution.landingPath,
    attribution_captured_at: attribution.capturedAt,
  };
}

function withRestoredAttribution(data?: UmamiEventData) {
  const restoredAttribution = getRestoredAttributionEventData();

  if (!restoredAttribution) {
    return data;
  }

  return {
    ...restoredAttribution,
    ...(data ?? {}),
  };
}

function readStoredAttributionForAnalytics(): StoredAttribution | null {
  if (typeof window === "undefined") {
    return null;
  }

  return readStoredAttributionFromStorage() ?? readStoredAttributionFromCookie();
}

function readStoredAttributionFromStorage(): StoredAttribution | null {
  try {
    const value = window.localStorage.getItem(LAST_TOUCH_ATTRIBUTION_STORAGE_KEY);

    return parseStoredAttribution(value);
  } catch {
    return null;
  }
}

function readStoredAttributionFromCookie(): StoredAttribution | null {
  if (typeof document === "undefined") {
    return null;
  }

  try {
    const value = getCookieValue(document.cookie, LAST_TOUCH_ATTRIBUTION_COOKIE);

    return parseStoredAttribution(value);
  } catch {
    return null;
  }
}

function parseStoredAttribution(value: string | null): StoredAttribution | null {
  if (!value) {
    return null;
  }

  try {
    const parsed = JSON.parse(decodeURIComponent(value)) as unknown;

    if (!isStoredAttribution(parsed)) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

function isStoredAttribution(value: unknown): value is StoredAttribution {
  if (!value || typeof value !== "object") {
    return false;
  }

  const candidate = value as Record<string, unknown>;

  return (
    isNullableString(candidate.utmSource) &&
    isNullableString(candidate.utmMedium) &&
    isNullableString(candidate.utmCampaign) &&
    isNullableString(candidate.utmContent) &&
    isNullableString(candidate.utmTerm) &&
    isNullableString(candidate.fbclid) &&
    isNullableString(candidate.gclid) &&
    isNullableString(candidate.referrer) &&
    isNullableString(candidate.landingPath) &&
    typeof candidate.capturedAt === "string"
  );
}

function isNullableString(value: unknown) {
  return value === null || typeof value === "string";
}

function getCookieValue(cookieHeader: string | null, key: string) {
  if (!cookieHeader) {
    return null;
  }

  const cookiePrefix = `${key}=`;
  const entries = cookieHeader.split(";");

  for (const entry of entries) {
    const trimmed = entry.trim();
    if (trimmed.startsWith(cookiePrefix)) {
      return trimmed.slice(cookiePrefix.length);
    }
  }

  return null;
}

function getUmamiQueue(): QueuedUmamiEvent[] {
  if (Array.isArray(window[umamiQueueKey])) {
    return window[umamiQueueKey];
  }

  window[umamiQueueKey] = [];
  return window[umamiQueueKey];
}

function trackUmami(eventName: string, data?: UmamiEventData) {
  if (typeof window.umami?.track !== "function") {
    getUmamiQueue().push({ eventName, eventData: data });
    return;
  }

  window.umami.track(eventName, data);
}

function trackPostHog(eventName: string, data?: UmamiEventData) {
  import("@/lib/posthog-client")
    .then(async ({ ensurePostHog }) => {
      const posthog = await ensurePostHog();
      posthog?.capture(eventName, data ?? {});
    })
    .catch(() => {
      // posthog-js not loaded; skip silently
    });
}

function trackGa4(eventName: string, data?: UmamiEventData) {
  if (ga4SuppressedEventNames.has(eventName)) {
    return;
  }

  const eventData = {
    ...(data ?? {}),
    hyred_event_name: eventName,
    ga4_event_name: eventName,
    ads_key_event: adsKeyEventNames.has(eventName),
    event_id: createEventId(eventName),
  };

  if (analyticsDelivery === "zaraz" && window.zaraz) {
    window.zaraz.track(eventName, eventData);
    return;
  }

  // GTM consumes Custom Events after its deferred loader starts.
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    ...eventData,
    event: eventName,
  });
}

export function trackEvent(eventName: string, data?: UmamiEventData) {
  const normalizedData = withRestoredAttribution(normalizeEventData(data));

  if (debugEnabled) {
    console.log("[analytics]", eventName, normalizedData ?? {});
  }

  if (!shouldTrack) {
    return;
  }

  trackUmami(eventName, normalizedData);
  trackGa4(eventName, normalizedData);
  trackPostHog(eventName, normalizedData);
}
